import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  LayoutDashboard,
  Briefcase,
  FileText,
  MessageSquare,
  User,
  Shield,
  ShieldCheck,
  Users,
  Settings,
} from 'lucide-react';
import { useAuthStore } from '../../stores/authStore';
import { useUIStore } from '../../stores/uiStore';
import { cn } from '../../lib/utils';

interface NavItem {
  label: string;
  href: string;
  icon: React.ReactNode;
}

// "Publicar trabajo", "Favoritos" y "Reviews" como pantallas propias no existen ni en la app
// Flutter (son botones/menú stub ahí también) — se omiten acá en vez de linkear a páginas vacías.
const clientNav: NavItem[] = [
  { label: 'Dashboard', href: '/', icon: <LayoutDashboard className="w-5 h-5" /> },
  { label: 'Mis Trabajos', href: '/my-jobs', icon: <FileText className="w-5 h-5" /> },
  { label: 'Mensajes', href: '/messages', icon: <MessageSquare className="w-5 h-5" /> },
  { label: 'Perfil', href: '/profile', icon: <User className="w-5 h-5" /> },
];

const workerNav: NavItem[] = [
  { label: 'Dashboard', href: '/', icon: <LayoutDashboard className="w-5 h-5" /> },
  { label: 'Perfil profesional', href: '/worker/profile', icon: <ShieldCheck className="w-5 h-5" /> },
  { label: 'Buscar Trabajos', href: '/worker/jobs', icon: <Briefcase className="w-5 h-5" /> },
  { label: 'Mis Propuestas', href: '/my-jobs', icon: <FileText className="w-5 h-5" /> },
  { label: 'Mensajes', href: '/messages', icon: <MessageSquare className="w-5 h-5" /> },
  { label: 'Perfil', href: '/profile', icon: <User className="w-5 h-5" /> },
];

const adminNav: NavItem[] = [
  { label: 'Dashboard', href: '/admin/dashboard', icon: <LayoutDashboard className="w-5 h-5" /> },
  { label: 'Usuarios', href: '/admin/users', icon: <Users className="w-5 h-5" /> },
  { label: 'Trabajos', href: '/admin/jobs', icon: <Briefcase className="w-5 h-5" /> },
  { label: 'Categorías', href: '/admin/categories', icon: <FileText className="w-5 h-5" /> },
  { label: 'Reportes', href: '/admin/reports', icon: <Shield className="w-5 h-5" /> },
  { label: 'Banners', href: '/admin/banners', icon: <Settings className="w-5 h-5" /> },
];

export const Sidebar: React.FC = () => {
  const { user } = useAuthStore();
  const { sidebarOpen, setSidebarOpen } = useUIStore();
  const location = useLocation();

  const getNavItems = () => {
    if (user?.user_type === 'admin') return adminNav;
    if (user?.user_type === 'worker') return workerNav;
    return clientNav;
  };

  const navItems = getNavItems();

  return (
    <>
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black/20 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}
      <aside
        className={cn(
          'hidden lg:flex flex-col w-60 h-full shrink-0 bg-white border-r border-secondary-200 overflow-y-auto',
          sidebarOpen ? 'flex' : ''
        )}
      >
        <nav className="flex-1 py-3 px-2 space-y-0.5">
          {navItems.map((item) => {
            const isActive = location.pathname === item.href || location.pathname.startsWith(item.href + '/');
            return (
              <Link
                key={item.href}
                to={item.href}
                onClick={() => setSidebarOpen(false)}
                className={cn(
                  'flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors',
                  isActive
                    ? 'bg-primary-50 text-primary-700'
                    : 'text-secondary-600 hover:bg-secondary-100 hover:text-secondary-900'
                )}
              >
                {item.icon}
                {item.label}
              </Link>
            );
          })}
        </nav>

        <div className="p-3 border-t border-secondary-200">
          <div className="bg-gradient-to-br from-primary-600 to-primary-700 rounded-xl p-4 text-white">
            <p className="text-sm font-medium">¿Necesitas ayuda?</p>
            <p className="text-xs text-primary-100 mt-0.5">Contacta a nuestro soporte</p>
            <button className="mt-2.5 px-3 py-1.5 bg-white/20 hover:bg-white/30 rounded-lg text-xs font-medium transition-colors">
              Soporte
            </button>
          </div>
        </div>
      </aside>
    </>
  );
};
